import type { AssetInstructionOut } from "kubb";
import type React from "react";
import { useMemo, useState } from "react";
import { TableCell, TableRow } from "../../../../components/Theme/table";
import { formatDate } from "../../../../components/utils/HighlightNullAndOldDates";
import ChildAssetLink from "./ChildAssetLink";
import {
  formatInstructionType,
  formatMethod,
  formatStatus,
  formatTrigger,
  getRelayActionInfo,
  getStatusClasses,
  getSyncTypeInfo,
  isAwaitingDevice,
  shouldShowCountdown,
  useCountdown,
} from "./instructionFormatters";

interface Props {
  instruction: AssetInstructionOut;
  viewedAssetId: string;
  companyId: string;
}

/**
 * Finds the current active step in a workflow.
 * Returns the step that is in_progress, pending_retry, or the next pending step.
 * If all steps are complete/failed/cancelled, returns the last non-pending step.
 */
function getCurrentStep(
  children: AssetInstructionOut[],
): AssetInstructionOut | null {
  if (children.length === 0) return null;

  // Sort by step_order to ensure correct order
  const sorted = [...children].sort(
    (a, b) => (a.step_order || 0) - (b.step_order || 0),
  );

  // Find the currently active step (in_progress or pending_retry)
  const activeStep = sorted.find(
    (c) => c.status === "in_progress" || c.status === "pending_retry",
  );
  if (activeStep) return activeStep;

  // Find the next pending step (next to run)
  const nextPending = sorted.find((c) => c.status === "pending");
  if (nextPending) return nextPending;

  // All steps are terminal - prioritize failed status over cancelled
  // When a step fails, subsequent steps are cancelled, so we want to show
  // the failed step's status rather than the last cancelled step
  const failedStep = sorted.find((c) => c.status === "failed");
  if (failedStep) {
    return failedStep;
  }

  // No failed step - find the last completed/cancelled
  const terminalSteps = sorted.filter(
    (c) => c.status === "success" || c.status === "cancelled",
  );
  if (terminalSteps.length > 0) {
    return terminalSteps[terminalSteps.length - 1];
  }

  return sorted[0];
}

/**
 * Determines if a workflow is currently active (has work in progress or pending).
 */
function isWorkflowActive(children: AssetInstructionOut[]): boolean {
  return children.some(
    (c) =>
      c.status === "in_progress" ||
      c.status === "pending_retry" ||
      c.status === "pending",
  );
}

/**
 * Chevron icon component for expand/collapse indicator
 */
function ChevronIcon({ expanded }: { expanded: boolean }): React.ReactElement {
  return (
    <svg
      className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

/**
 * Child instruction row component - renders with indentation and live countdown
 */
function ChildInstructionRow({
  instruction,
  stepNumber,
}: {
  instruction: AssetInstructionOut;
  stepNumber: number;
}): React.ReactElement {
  const countdown = useCountdown(instruction.scheduled_at);
  const showCountdown =
    countdown &&
    shouldShowCountdown(instruction.status, instruction.configuration_method);
  const awaitingDevice = isAwaitingDevice(
    instruction.status,
    instruction.configuration_method,
  );
  const syncTypeInfo = getSyncTypeInfo(instruction);
  const relayActionInfo = getRelayActionInfo(instruction);

  return (
    <TableRow className="bg-gray-50 dark:bg-gray-800/50">
      <TableCell className="align-top pl-6">
        <div className="flex items-center gap-2">
          <span className="inline-flex w-5 justify-end text-gray-400">
            {stepNumber}.
          </span>
          <span>
            {instruction.instruction_type === "WAIT" &&
            instruction.options &&
            "description" in instruction.options &&
            instruction.options.description
              ? String(instruction.options.description)
              : formatInstructionType(instruction.instruction_type)}
          </span>
          {syncTypeInfo && (
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${syncTypeInfo.classes}`}
              title={
                syncTypeInfo.label === "Targeted"
                  ? "Targeted sync: Only changed values will be sent"
                  : "Full sync: All configuration values will be sent"
              }
            >
              {syncTypeInfo.label}
            </span>
          )}
          {relayActionInfo && (
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${relayActionInfo.classes}`}
              title={relayActionInfo.tooltip}
            >
              {relayActionInfo.label}
            </span>
          )}
        </div>
      </TableCell>
      {/* Asset column empty for workflow child rows - inherited from parent */}
      <TableCell className="align-top" />
      <TableCell className="align-top">
        {formatMethod(instruction.configuration_method)}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-1">
          <span className={getStatusClasses(instruction.status)}>
            {formatStatus(instruction.status)}
          </span>
          {showCountdown && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {countdown}
            </span>
          )}
          {awaitingDevice && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Awaiting device
            </span>
          )}
        </div>
      </TableCell>
      {/* Trigger, User, Created At columns empty for child rows - inherited from parent */}
      <TableCell className="align-top" />
      <TableCell className="align-top" />
      <TableCell className="align-top">
        {instruction.current_attempt}/{instruction.max_attempts}
      </TableCell>
      <TableCell className="align-top" />
      <TableCell className="align-top">
        <span className="text-nowrap">
          {formatDate(instruction.updated_at)}
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * Workflow instruction row with expandable accordion showing child steps.
 * Renders a parent workflow row with expand/collapse control and nested child rows.
 * Supports workflows with any number of steps (2-step SYNC_CONFIG or 5-step Factory Reset).
 *
 * Features:
 * - Auto-expands when workflow is active (in_progress, pending_retry, or pending steps)
 * - Shows status of the current/next step rather than aggregate status
 * - Live countdown timer for scheduled steps
 * - Hides attempts column for parent row (not meaningful for workflows)
 * - Shows sync type badge (Full/Targeted) for configuration syncs
 * - Shows relay action badge (Turn On/Turn Off) for SWITCH_RELAY instructions
 */
export default function WorkflowInstructionRow(
  props: Props,
): React.ReactElement {
  const { instruction, viewedAssetId, companyId } = props;
  const children = instruction.children || [];
  const hasChildren = children.length > 0;

  // Determine if this is a child device instruction shown on gateway view
  const isChildDeviceInstruction =
    instruction.parent_asset_id &&
    instruction.parent_asset_id === viewedAssetId &&
    instruction.asset_id !== viewedAssetId;

  // Determine if workflow is active to auto-expand
  const workflowActive = useMemo(
    () => hasChildren && isWorkflowActive(children),
    [hasChildren, children],
  );

  // Auto-expand if workflow is active
  const [expanded, setExpanded] = useState(workflowActive);

  // Get the current step for status display
  const currentStep = useMemo(
    () => (hasChildren ? getCurrentStep(children) : null),
    [hasChildren, children],
  );

  // Display status based on current step (for workflows) or instruction status
  const displayStatus = currentStep?.status || instruction.status;

  // Get sync type info for the parent instruction
  const syncTypeInfo = getSyncTypeInfo(instruction);

  // Get relay action info for the parent instruction
  const relayActionInfo = getRelayActionInfo(instruction);

  const handleToggle = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (hasChildren && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setExpanded(!expanded);
    }
  };

  return (
    <>
      <TableRow
        className={
          hasChildren
            ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
            : ""
        }
        onClick={handleToggle}
        onKeyDown={hasChildren ? handleKeyDown : undefined}
        role={hasChildren ? "button" : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        aria-label={
          hasChildren
            ? expanded
              ? "Collapse workflow steps"
              : "Expand workflow steps"
            : undefined
        }
      >
        <TableCell className="align-top">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-5 justify-center text-gray-400">
              {hasChildren ? <ChevronIcon expanded={expanded} /> : "•"}
            </span>
            <span>{formatInstructionType(instruction.instruction_type)}</span>
            {syncTypeInfo && (
              <span
                className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${syncTypeInfo.classes}`}
                title={
                  syncTypeInfo.label === "Targeted"
                    ? "Targeted sync: Only changed values will be sent"
                    : "Full sync: All configuration values will be sent"
                }
              >
                {syncTypeInfo.label}
              </span>
            )}
            {relayActionInfo && (
              <span
                className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${relayActionInfo.classes}`}
                title={relayActionInfo.tooltip}
              >
                {relayActionInfo.label}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="align-top">
          {isChildDeviceInstruction ? (
            <ChildAssetLink
              assetId={instruction.asset_id}
              companyId={companyId}
            />
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </TableCell>
        <TableCell className="align-top text-gray-400 dark:text-gray-500">
          {hasChildren ? "-" : formatMethod(instruction.configuration_method)}
        </TableCell>
        <TableCell className="align-top">
          {(!hasChildren || !expanded) && (
            <span className={getStatusClasses(displayStatus)}>
              {formatStatus(displayStatus)}
            </span>
          )}
        </TableCell>
        <TableCell className="align-top">
          {formatTrigger(instruction.trigger)}
        </TableCell>
        <TableCell className="align-top">
          {instruction.user_email || "System"}
        </TableCell>
        <TableCell className="align-top text-gray-400 dark:text-gray-500">
          {hasChildren
            ? "-"
            : `${instruction.current_attempt}/${instruction.max_attempts}`}
        </TableCell>
        <TableCell className="align-top">
          <span className="text-nowrap">
            {formatDate(instruction.created_at)}
          </span>
        </TableCell>
        <TableCell className="align-top">
          <span className="text-nowrap">
            {formatDate(instruction.updated_at)}
          </span>
        </TableCell>
      </TableRow>
      {expanded &&
        children.map((child, index) => (
          <ChildInstructionRow
            key={child.id}
            instruction={child}
            stepNumber={child.step_order || index + 1}
          />
        ))}
    </>
  );
}
